/** Activation → Agreements: status per required agreement, open the package review, download accepted PDFs. */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { btnPrimary, btnSmall, errorText } from '../finance/financeUi';
import { AgreementPackageReview } from './AgreementPackageReview';
import { openPdf } from './agreementShared';

const LABEL = {
  ACTION_REQUIRED: ['Review & accept', 'bg-amber-100 text-amber-900'], REACCEPT_REQUIRED: ['New version — accept again', 'bg-amber-100 text-amber-900'],
  SCHEDULE_ACK_REQUIRED: ['Schedule A changed — acknowledge', 'bg-amber-100 text-amber-900'], PENDING_SIGNATURE: ['Waiting for your e-signature', 'bg-sky-100 text-sky-900'],
  PREPARING: ['MSC is preparing this agreement', 'bg-muted'], ACCEPTED: ['Accepted', 'bg-emerald-100 text-emerald-800'],
};
const OPEN = ['ACTION_REQUIRED', 'REACCEPT_REQUIRED', 'SCHEDULE_ACK_REQUIRED'];

export function AgreementPackagesStep({ onChanged, refreshKey, saasInBilling = true }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.request('/activation/agreement-packages').then((d) => setRows(d.agreements)).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!rows) return null;
  if (open) return <AgreementPackageReview agreementType={open} onCancel={() => setOpen(null)} onDone={() => { setOpen(null); load(); onChanged?.(); }} />;
  const refresh = async (pkg) => {
    try { const r = await api.request(`/activation/agreement-packages/${pkg.package_id}/refresh-signature`, { method: 'POST' }); toast.message(r.status === 'ACCEPTED' ? 'Signed — package stored' : 'Not signed yet'); load(); onChanged?.(); } catch (e) { toast.error(errorText(e)); }
  };
  const pdf = (pkg) => openPdf(`/me/agreement-packages/${pkg.package_id}/pdf`).catch((e) => toast.error(errorText(e)));
  return (
    <div className="space-y-2" data-testid="act-agreements">
      {rows.map((r) => {
        const [text, cls] = LABEL[r.status] || [r.status, 'bg-muted'];
        return (
          <div key={r.agreement_type} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm" data-testid={`agreement-row-${r.agreement_type}`}>
            <div><p className="font-medium">{r.current_version?.title || r.name}</p>
              <p className="text-xs text-muted-foreground">{r.current_version ? `Version ${r.current_version.version}` : 'Not published yet'}{r.accepted_package ? ` · accepted v${r.accepted_package.agreement_version} on ${new Date(r.accepted_package.accepted_at).toLocaleDateString()}` : ''}</p></div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-xs ${cls}`} data-testid={`agreement-status-${r.agreement_type}`}>{text}</span>
              {r.accepted_package && <button type="button" className={btnSmall} onClick={() => pdf(r.accepted_package)} data-testid={`agreement-pdf-${r.agreement_type}`}>Download PDF</button>}
              {r.status === 'PENDING_SIGNATURE' && <button type="button" className={btnSmall} onClick={() => refresh(r.pending_package)} data-testid={`agreement-refresh-${r.agreement_type}`}>Check signature</button>}
              {r.agreement_type === 'msc_agent' && <a href="/agreement" className={btnPrimary} data-testid="agreement-open-center">Open Agreement Center</a>}
              {r.agreement_type === 'software_subscription' && r.status !== 'ACCEPTED' && saasInBilling && <span className="text-xs text-muted-foreground" data-testid="agreement-saas-at-billing">Accepted when you set up billing</span>}
              {OPEN.includes(r.status) && !(r.agreement_type === 'msc_agent' || (r.agreement_type === 'software_subscription' && saasInBilling)) && <button type="button" className={btnPrimary} onClick={() => setOpen(r.agreement_type)} data-testid={`agreement-open-${r.agreement_type}`}>Review</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MyDocuments({ refreshKey }) {
  const [docs, setDocs] = useState([]);
  useEffect(() => { api.request('/me/documents').then((d) => setDocs(d.documents)).catch(() => setDocs([])); }, [refreshKey]);
  return (
    <div className="divide-y" data-testid="my-documents">
      {!docs.length && <p className="py-2 text-sm text-muted-foreground">Signed agreement packages appear here.</p>}
      {docs.map((d) => (
        <div key={d.document_id} className="flex items-center justify-between gap-2 py-2 text-sm" data-testid={`my-document-${d.document_id}`}>
          <div><p>{d.title}</p><p className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleString()} · SHA-256 {d.sha256.slice(0, 12)}…</p></div>
          <button type="button" className={btnSmall} onClick={() => openPdf(`/me/documents/${d.document_id}/download`).catch((e) => toast.error(errorText(e)))} data-testid={`my-document-open-${d.document_id}`}>Open</button>
        </div>
      ))}
    </div>
  );
}
